import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Navigate, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {APIClient} from '../../../../utils/APIClient';
import {API_URL} from '../../../../settings/local_settings';
import {AccessService} from '../../../../utils/AccessService';
import {useFicha21} from '../../../../context/UserModulesContext';
import {useBoosterFlow} from '../../../../context/BoosterFlowContext';
import {usePendingFactsFor, usePendingWork} from '../../../../context/PendingWorkContext';
import {ClientMemoryApi} from '../../../../api/clientMemoryApi';
import {Spinner} from '../../../Spinner';
import {ClientHeader} from '../../ClientHeader';
import {ClientTabs} from '../../ClientTabs';
import {BoosterThread} from '../BoosterStage';
import {BoosterFeedHero} from '../BoosterFeedHero';
import {KIND_AUTO} from '../BoosterOmnibar';
import {BoosterUseIaBand} from '../BoosterUseIaBand';
import {ClientFactsProvider, useClientFacts} from '../facts/ClientFactsStore';
import {SourceViewerHost} from '../SourceViewerHost';
import {BoosterReviewDeck} from '../review/BoosterReviewDeck';
import {FeedActContent} from '../feed/FeedActContent';
import {FeedGuideBlock} from '../feed/FeedGuideBlock';
import {MemoryDuo} from '../memoria/MemoryDuo';
import {SourcesDrawer} from '../feed/SourcesDrawer';
import {SourcesDrawerContext} from '../feed/sourcesDrawerContext';
import {SOURCES_ANCHOR} from '../feed/SourceLine';
import {BoosterConnectionModal, CONN_ACCOUNT, CONN_IA} from '../BoosterConnectionModal';
import {useClientPromptLibrary} from '../use_ia/useClientPromptLibrary';
import {useClientMemoryAutonomy} from '../../memory/useMemoryAutonomy';
import {
    BoosterHelpButtons, BoosterHelpModal, HELP_GLOSSARY, HELP_SHORTCUTS, isHelpShortcut,
} from './BoosterHelp';
import {BoosterActKicker} from './BoosterActKicker';
import {BoosterAurora} from './BoosterAurora';
import {BoosterThreshold} from './BoosterThreshold';
import {LazySection} from './LazySection';
import {useHashScroll} from './useHashScroll';
import {useRevealOnScroll} from './useRevealOnScroll';
import {useSectionObserver} from './useSectionObserver';
import {
    BOOSTER_ANCHOR_BITACORA,
    BOOSTER_STEP_FEED,
    BOOSTER_STEP_MEMORY,
    BOOSTER_STEP_REVIEW,
    BOOSTER_STEP_USE_IA,
    boosterStepFromHash,
    hasBitacoraDeepLink,
    boosterStepsFor,
} from '../boosterSteps';

// Orden del recorrido. `USE_IA` no entra: es el destino, vive del otro lado de la apertura.
const WALK = [BOOSTER_STEP_FEED, BOOSTER_STEP_REVIEW, BOOSTER_STEP_MEMORY];

// Alto reservado por cada panel diferido mientras no está montado. No busca ser exacto: busca que
// la barra de scroll no mienta y que montar uno no dé un salto grande.
const PANEL_MIN_HEIGHT = {facts: 480, bitacora: 520};

// La Bitácora lee `?layers=&date_from=&date_to=` en su CONSTRUCTOR (`readDeepLinkParams`) y ese es
// el contrato con el que el Panel, el Listado v2, la campanita y el Command Center la abren ya
// filtrada. Si se montara diferida, el constructor correría cuando el usuario ya está mirando otra
// cosa —o después de que alguien limpie la query— y el deep-link se perdería en silencio.
//
// La condición es la MISMA que usa el duo para abrir en la bandeja de Historial (`boosterSteps.js`):
// son dos puertas que tienen que abrirse las dos, y con la regla duplicada alcanzaba con tocar una
// para que el deep-link quedara a medias sin que nada fallara a la vista.
function bitacoraDeepLinkInUrl() {
    return hasBitacoraDeepLink(window.location.search, window.location.hash);
}

/**
 * El puente al store de hechos.
 *
 * El contenedor MONTA el provider, así que no puede usar `useClientFacts()` (estaría afuera). Este
 * componente de una línea vive adentro y publica hacia afuera las dos cosas que el contenedor no
 * puede leer solo: el `refetch` —lo que el vigía de extracciones necesita para volver a pedir los
 * hechos cuando un documento termina— y si el listado FALLÓ.
 *
 * Lo segundo hace falta porque `onApprovedCountChange` publica `null` tanto mientras carga como
 * cuando no se pudo leer, y el orbe dibuja esas dos cosas distinto: late como si el número
 * estuviera por llegar, o pone un guion. Latir para siempre es prometer un número que no va a venir.
 */
function FactsStoreBridge({refetchRef, onLoadFailedChange}) {
    const {actions, loadFailed} = useClientFacts();
    useEffect(() => {
        refetchRef.current = actions.refetch;
        return () => { refetchRef.current = null; };
    }, [actions.refetch, refetchRef]);
    useEffect(() => {
        if (onLoadFailedChange) onLoadFailedChange(loadFailed);
    }, [loadFailed, onLoadFailedChange]);
    return null;
}

/**
 * El Recorrido entero en una página: Alimenta → Revisa → Memoria → Usa tu IA, con los dos umbrales
 * entre medio.
 *
 * Los umbrales son la razón de ser de esto. "Lo que Dashbo encuentra baja aquí a revisión" y "lo
 * que apruebas entra a la memoria" sólo se pueden decir entre dos secciones contiguas: con cuatro
 * rutas separadas, la relación causal entre los actos era algo que había que explicar en un lede,
 * y acá se ve.
 *
 * Cada acto renderiza la vista que YA existe, sin re-tematizarla: el escenario es el escenario
 * —oscuro, con el hilo y los umbrales— y los listados de siempre siguen en su panel claro.
 *
 * El scroll lo gobiernan tres piezas, cada una en su archivo: `useSectionObserver` publica qué acto
 * se está mirando (lo lee el sidebar, que es hermano del contenido), `useHashScroll` lleva la
 * página al ancla al entrar y en cada cambio de hash, y `LazySection` difiere el montaje de los
 * paneles pesados. La furniture oscura —heads, orbe, umbrales, hilo, apertura— monta SIEMPRE: sus
 * números salen del contexto y del brief, o sea que están desde el primer paint y diferirla sólo
 * conseguiría que el Recorrido se dibuje a pedazos.
 */
export function BoosterFlowContent({clientId}) {
    const {t} = useTranslation();
    const location = useLocation();
    const {ficha21, clientMemoryLevel, modules, loaded, clientEconomicsEnabled} = useFicha21();
    const {activeSection} = useBoosterFlow();
    const pendingFacts = usePendingFactsFor(clientId);
    const {overridePendingFacts} = usePendingWork();
    const flowRef = useRef(null);
    // El `refetch` del store, publicado por `FactsStoreBridge` (que sí está adentro del provider).
    const factsRefetchRef = useRef(null);

    const [name, setName] = useState('');
    // Los hechos del brief, no cuántos son: la vista "nube" del carrusel del orbe los dibuja. Se
    // guardaban y se tiraba todo menos el `length`, así que esa vista no se ofrecía NUNCA — el
    // carrusel decidía mostrarla con `facts && facts.length`, y nadie le pasaba el array.
    // El NÚMERO ya no sale de acá: ver `approved`.
    const [briefFacts, setBriefFacts] = useState(null);
    // Cuántos hechos esperan una decisión, según el store del mazo (`ClientFactsStore`). `null`
    // mientras no haya hablado. Ver `pendingCount`, más abajo.
    const [factsPending, setFactsPending] = useState(null);
    // Cuánto ruido hay. Sólo lo usa el lede del acto Revisa, para no decir «no hay nada esperando
    // tu ok» cuando la cola principal está en cero pero el modo «Ruido probable» tiene material.
    const [factsNoise, setFactsNoise] = useState(0);
    // El listado de hechos no se pudo leer. Lo publica el store (`FactsStoreBridge`), no el brief:
    // desde que el conteo sale del store, quien puede fallar y dejar al orbe sin número es el store.
    const [factsFailed, setFactsFailed] = useState(false);
    /**
     * Los hechos aprobados que hay AHORA, tal como los cuenta el store (`library`).
     *
     * Es el número de la página: el umbral 2, el orbe del acto Alimenta y su chip del HUD. Se mueve
     * con cada aprobación, fusión, tanda y deshacer, porque es la misma lista que dibuja el archivo
     * del acto Memoria.
     *
     * El orbe salía del brief, que se pide UNA vez y con `{cache: true}`: aprobabas siete hechos en
     * Revisa y el chip seguía diciendo lo de antes, treinta píxeles más arriba del mazo que los
     * acababa de mover. Eran otra vez tres números para la misma pregunta — justo lo que el duo
     * unificó cuando pasó a leer `library.length`. El brief NO se fue: sigue trayendo los hechos que
     * dibuja la nube del carrusel, que es otra cosa.
     */
    const [approved, setApproved] = useState({n: null, truncated: false});
    const [ingestToken, setIngestToken] = useState(0);
    const [processingState, setProcessingState] = useState(null);
    const processingUnknown = !processingState || processingState.clientId !== clientId
        || processingState.loading || processingState.stalled || processingState.unknown;
    const extractionActive = !!processingState && processingState.clientId === clientId && processingState.active;
    // Refresca el Paquete de Contexto de la guía de arriba del acto (`FeedGuideBlock`). Sube en la
    // MISMA transición a READY que antes lo subía adentro del acto: un documento listo ya tiene su
    // `document_kind` y puede haber tachado un ítem. No sube con cada ingesta, porque el tipo recién
    // se conoce al terminar la extracción.
    const [packToken, setPackToken] = useState(0);
    // El tipo de material que la Barra Total va a declarar, y el pedido de foco.
    //
    // Vive ACÁ y no en ninguno de los dos porque quien lo ELIGE (una tarjeta del instructivo, en
    // `FeedGuideBlock`) y quien lo USA (la Barra Total, en `BoosterFeedHero`) son HERMANOS. Antes no
    // hacía falta: la tarjeta abría el compositor, que estaba en su mismo componente. Al sacar el
    // compositor (card ScPxmmB3) el estado tuvo que subir al único que ve a los dos.
    const [composeKind, setComposeKind] = useState(KIND_AUTO);
    const [omnibarFocus, setOmnibarFocus] = useState(0);
    // El último documento que entró, para que el acto pueda narrarlo. Va junto al token y no
    // adentro de él porque el token también lo sube el refresco, que no tiene documento que nombrar.
    const [lastIngested, setLastIngested] = useState(null);
    // Una tarjeta del instructivo: elige el tipo y manda el foco a la barra, que está más arriba.
    // El token sube SIEMPRE, aunque se toque dos veces la misma tarjeta — si no, el segundo clic no
    // haría nada visible y se lee como que la pantalla se colgó.
    const handlePickKind = useCallback((picked) => {
        setComposeKind(picked);
        setOmnibarFocus(token => token + 1);
    }, []);
    // `null` = todavía no lo sé. El chip «Tu IA» del HUD dibuja los tres estados distinto, y
    // arrancar en `false` le diría "por conectar" a toda agencia que sí lo tiene andando durante
    // el segundo que tarda el starter pack.
    const [mcpConnected, setMcpConnected] = useState(null);
    // ¿La URL pide la Bitácora? Con eso el panel del acto Memoria monta sin esperar al scroll.
    // Se resuelve al montar y CADA VEZ QUE ENTRA un deep-link nuevo: el usuario que ya está en el
    // Recorrido y hace click en la campanita no remonta la ruta, y con esto resuelto una sola vez el
    // panel se quedaba diferido justo cuando alguien acababa de pedirlo.
    // Nunca vuelve a `false`: la Bitácora limpia la query cuando el usuario saca el strip de
    // "filtros aplicados", y eso no puede desmontar nada abajo del mouse.
    const [bitacoraDeepLink, setBitacoraDeepLink] = useState(bitacoraDeepLinkInUrl);
    useEffect(() => {
        if (hasBitacoraDeepLink(location.search, location.hash)) setBitacoraDeepLink(true);
    }, [location.search, location.hash]);

    // El panel «Documentos y notas» (mockup: `openDocs`). La tabla de Fuentes ya NO vive inline en
    // el acto Alimenta: se abre a pedido desde la línea de fuentes, desde el botón de Drive de la
    // Barra Total, desde el archivo de Memoria y desde el modal de la cuenta — por eso la manija
    // viaja por contexto y no por props. `connectToken` sube cuando además hay que abrir el Picker.
    const [sourcesOpen, setSourcesOpen] = useState(false);
    const [sourcesConnectToken, setSourcesConnectToken] = useState(0);
    const openSources = useCallback((options) => {
        setSourcesOpen(true);
        if (options && options.connect) setSourcesConnectToken(token => token + 1);
    }, []);
    const closeSources = useCallback(() => setSourcesOpen(false), []);
    const drawerApi = useMemo(() => ({available: true, openSources}), [openSources]);

    // Las dos puertas viejas al material siguen abiertas: `#fuentes` (el ancla que usaba la línea
    // de fuentes, y lo que ve un link copiado) y `?connect=drive` (el atajo del menú "+ Agregar",
    // que además abre el Picker — eso lo resuelve la vista de conexiones leyendo la query, como
    // siempre). Las dos se traducen en abrir el panel al aterrizar, y otra vez cada vez que la URL
    // vuelve a pedirlas (`location.key` cambia aunque el hash sea el mismo).
    useEffect(() => {
        const hash = String(location.hash || '').replace(/^#/, '');
        let wantsDrive = false;
        try {
            wantsDrive = new URLSearchParams(location.search || '').get('connect') === 'drive';
        } catch (e) {
            // Una query que el navegador no puede parsear no pide nada.
        }
        if (hash === SOURCES_ANCHOR || wantsDrive) setSourcesOpen(true);
    }, [location.search, location.hash, location.key]);

    // La ayuda («¿Qué es cada cosa?» / «Atajos») y los dos chips de estado del HUD («Tu IA» / «La
    // cuenta»): overlays chicos, un estado cada uno.
    const [helpMode, setHelpMode] = useState(null);
    const [connMode, setConnMode] = useState(null);

    const steps = useMemo(
        () => boosterStepsFor({clientMemoryLevel, modules}),
        [clientMemoryLevel, modules],
    );
    const hasStep = useCallback((key) => steps.some(s => s.key === key), [steps]);

    const showFeed = hasStep(BOOSTER_STEP_FEED);
    const showReview = hasStep(BOOSTER_STEP_REVIEW);
    const showMemory = hasStep(BOOSTER_STEP_MEMORY);
    const showUseIa = hasStep(BOOSTER_STEP_USE_IA);
    const walkSteps = WALK.filter(hasStep);
    // La capa de Hechos y el acto Revisa son la MISMA puerta —nivel TOTAL, resuelta una sola vez en
    // `boosterSteps.js`—: donde no hay cola de curado tampoco hay hechos que pedir. Sin esto, una
    // agencia con la Memoria en OFF (donde el acto Memoria sobrevive por la Bitácora) disparaba los
    // tres endpoints de hechos para un nivel que no tiene ninguno.
    const hasFactsLayer = showReview;

    // Los actos presentes, en orden de página. El destino entra: es una sección más para el
    // observer (y es la que el fondo del scroll fuerza).
    const sectionIds = useMemo(
        () => [BOOSTER_STEP_FEED, BOOSTER_STEP_REVIEW, BOOSTER_STEP_MEMORY, BOOSTER_STEP_USE_IA]
            .filter(hasStep),
        [hasStep],
    );
    // Los hooks de scroll sólo tienen sentido cuando el Recorrido se está dibujando de verdad:
    // antes de `loaded` no hay anclas, y sin ficha 2.1 esta ruta redirige.
    const flowIsUp = loaded && ficha21 && steps.length > 0;

    useHashScroll(location, {enabled: flowIsUp});
    useSectionObserver(flowRef, flowIsUp ? sectionIds : []);
    // Los bloques marcados con `iab-rv` aparecen al entrar en pantalla (mockup: `.rv`).
    useRevealOnScroll(flowRef, flowIsUp);

    useEffect(() => {
        let alive = true;
        new APIClient(`${API_URL}client/${clientId}`).call('GET').then((data) => {
            if (alive && data && data.status === 'OK') setName(data.data.name);
        });
        return () => { alive = false; };
    }, [clientId]);

    // Cambiar de cliente sin desmontar la pantalla (los links del sidebar lo permiten) dejaría el
    // conteo del cliente ANTERIOR hasta que el store del nuevo termine de cargar. Vuelve a `null`,
    // que es «no sé», y mientras tanto manda el poller — que ya sabe el número de este cliente.
    useEffect(() => { setFactsPending(null); }, [clientId]);

    // UN solo pedido del brief para toda la página. Antes lo pedían el escenario (en Alimenta) y
    // el Resumen (en Memoria) por separado, que en cuatro rutas eran dos pantallas distintas; acá
    // conviven en el mismo DOM, así que sin esto sería el mismo request dos veces. `cache: true`
    // hace que el Resumen comparta el que ya está en vuelo.
    // La dependencia es el booleano, NUNCA `hasStep`: su identidad cambia con `modules`, que cambia
    // al menos dos veces por carga (el cache de localStorage primero, la red después). Con la
    // función en las deps, todo efecto que la use se corre dos veces por visita — el brief zafa por
    // el `{cache: true}`, pero el starter pack de acá abajo no, y se pedía dos veces.
    useEffect(() => {
        if (!clientId || !showFeed) return undefined;
        let alive = true;
        ClientMemoryApi.getMemoryBrief(clientId, {cache: true}).then((data) => {
            if (!alive) return;
            // Sólo la nube del carrusel. Si el brief no se pudo leer, `briefFacts` se queda en
            // `null` y esa vista no se ofrece — el conteo no depende de esto.
            if (data && data.status === 'OK' && data.data) setBriefFacts(data.data.facts || []);
        }).catch(() => {});
        return () => { alive = false; };
    }, [clientId, showFeed]);

    // ¿Ya usó su IA con este cliente? La banda de cierre lo afirma en pantalla, así que el dato
    // tiene que venir del backend y no de un default: sin esto le decía "todavía no conectaste tu
    // IA" a toda agencia que sí la tenía andando. Sale del mismo starter pack que usa el tab IA.
    useEffect(() => {
        if (!clientId || !showUseIa) return undefined;
        let alive = true;
        new APIClient(`${API_URL}agent/client_starter_pack/?agency_client_id=${clientId}`)
            .call('GET')
            .then((data) => {
                if (alive && data) setMcpConnected(!!data.mcp_recently_used);
            })
            .catch(() => {});
        return () => { alive = false; };
    }, [clientId, showUseIa]);

    // El inventario de lo que la IA puede usar con este cliente (`available` / `missing`), para
    // los chips del HUD y su modal. Es el MISMO hook que usa la banda de cierre, con `cache: true`:
    // dos consumidores, un request. Sin destino no se pide: el HUD no dibuja «Tu IA» sin él.
    const library = useClientPromptLibrary(showUseIa ? clientId : null,
        {economicsEnabled: clientEconomicsEnabled});
    // El knob «¿Cuánto aprueba Dashbo solo?» se pide UNA vez por página y se baja: el rail lo edita
    // y el lede del acto Revisa lo lee. Dos consumidores del mismo dato en el mismo DOM — el mismo
    // motivo por el que el brief se pide con `cache: true`. Sin acto Revisa (MEDIO) no se pide.
    const autonomy = useClientMemoryAutonomy(clientId, {enabled: showReview});

    // `?` abre los atajos del mazo (o el glosario, donde no hay mazo). Mismas guardas que el
    // teclado del mazo: no mientras se escribe, no con otro modal abierto.
    useEffect(() => {
        if (!flowIsUp) return undefined;
        const onKeyDown = (e) => {
            if (!isHelpShortcut(e)) return;
            e.preventDefault();
            setHelpMode(showReview ? HELP_SHORTCUTS : HELP_GLOSSARY);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [flowIsUp, showReview]);

    // Moverse entre actos dentro de la página: es scroll, no navegación. Se usa para los CTAs que
    // cruzan de un acto a otro ("ver la memoria" desde el mazo vacío).
    const scrollToAct = useCallback((stepKey) => {
        const el = document.getElementById(stepKey);
        if (!el) return;
        const smooth = !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
        el.scrollIntoView({behavior: smooth ? 'smooth' : 'auto', block: 'start'});
    }, []);

    /**
     * Entró material por la Barra Total o por el drop sobre el orbe.
     *
     * Sube el token —el acto Alimenta refresca su listado— y además guarda el documento, porque el
     * narrador («Leyendo “X”…») vive dentro del acto y desde acá no se lo puede hacer hablar de otra
     * forma. Esa frase la emitía el compositor plegado, que era la única puerta que vivía adentro
     * del acto; al sacarlo (card ScPxmmB3) se fue con él y el narrador quedó MUDO en toda la ventana
     * entre la subida y el READY —los ~8 s largos que la pieza existe para cubrir—, sin que nadie lo
     * hubiera decidido. Ahora la emiten las dos puertas que quedaron, que además es más de lo que
     * había: desde el orbe nunca se había narrado.
     */
    const handleIngested = useCallback((doc) => {
        setIngestToken(token => token + 1);
        setLastIngested(doc?.id ? doc : null);
        setProcessingState({clientId, active: ['PENDING', 'EXTRACTING'].includes(doc?.status),
            loading: true, stalled: false});
    }, [clientId]);

    /**
     * Un documento de esta visita terminó de extraerse: los hechos nuevos ya existen del otro lado.
     *
     * Dos cosas tienen que enterarse. El **store**, porque el mazo de Revisa está treinta píxeles
     * más abajo en la misma página y sin esto seguía vacío hasta un F5 — que es justo la cadena
     * que narra el umbral ("lo que Dashbo encuentra baja aquí a revisión"). Y la **tabla de
     * Fuentes**, que en el Recorrido tiene el poll apagado (lo hace el acto Alimenta, que es quien
     * muestra los estados en vivo) y refresca por `refreshToken`.
     *
     * Quien avisa es el propio acto Alimenta, que ya pollea y ya calcula la transición para el
     * narrador. Antes esto era un vigía con su PROPIO poll del mismo endpoint: dos consultas para
     * un dato que ya estaba resuelto a treinta líneas de distancia.
     */
    const handleDocumentReady = useCallback(() => {
        if (factsRefetchRef.current) factsRefetchRef.current();
        setIngestToken(token => token + 1);
        setPackToken(token => token + 1);
    }, []);

    /**
     * Los hechos del cliente cambiaron desde AFUERA del mazo: hoy, porque se borró un documento con
     * lo que se había extraído de él (card VLtk3vc7).
     *
     * No sirve `handleIngested`: ése sube el token —que refresca listados— pero **no toca el store
     * de hechos**, y el mazo de Revisa vive treinta píxeles más abajo en esta misma página. El QA lo
     * encontró así: se borraba el documento, el backend se llevaba sus hechos, y la carta seguía en
     * pantalla con sus botones «Aprobar» y «Rechazar» — se podía curar un hecho que ya no existía.
     * Tampoco sirve pasar por `handleDocumentReady`: aquél además narra («Leyendo “X”…»), y un
     * borrado no es una lectura.
     */
    const handleFactsChanged = useCallback(() => {
        if (factsRefetchRef.current) factsRefetchRef.current();
        setIngestToken(token => token + 1);
    }, []);

    // El store del mazo publica su conteo acá Y en el contexto global. Las dos cosas hacen falta y
    // no son la misma: el contexto corrige los badges de TODA la app (sidebar, campana, listado) de
    // forma optimista, pero el poller le limpia el override en cada respuesta exitosa; el state
    // local es el que esta página usa para hablar de SU cola.
    const handlePendingCountChange = useCallback((n, noise = 0) => {
        setFactsPending(n);
        setFactsNoise(noise);
        overridePendingFacts(clientId, n);
    }, [clientId, overridePendingFacts]);
    const handleApprovedCountChange = useCallback(
        (n, truncated) => setApproved({n, truncated}),
        [],
    );
    const handleFactsLoadFailed = useCallback((failed) => setFactsFailed(failed), []);

    // 🔴 UN solo número de pendientes en toda la pantalla. El store del mazo manda; el poller
    // global es el fallback de mientras carga.
    //
    // El HUD («N por revisar»), el umbral («N esperan tu ok»), el badge del hilo y el rail del mazo
    // dicen lo mismo con distintas palabras, y hasta el 08/09 salían de dos fuentes: éstos tres del
    // poller (`agent/pending-work/`, que refresca cada 15 s con algo en vuelo y cada 5 min sin
    // nada) y el rail del store. El store corrige el contexto al curar, pero **cada respuesta del
    // poller limpia ese override** (`setFactOverrides({})`): una respuesta que salió ANTES de la
    // aprobación aterriza DESPUÉS y repone el número viejo. Eso es lo que Pablo leyó como «5
    // esperando» y «4 esperando» en la misma pantalla.
    //
    // El store es el que tiene razón acá: es la misma lista que se está curando, con mutación
    // optimista y rollback ante error. `null` = todavía no habló (no es cero: afirmar 0 mientras
    // carga apagaría el badge y el umbral por un instante en cada entrada).
    //
    // ⚠️ `factsFailed` va en la MISMA condición que el `null`, y no es una precaución teórica: ante
    // `LOAD_FAILED` el store deja `loading: false` con `facts: []`, así que publica **0** y
    // `factsPending` deja de ser `null` — el fallback al poller no se usaría nunca. Sin esto, a
    // cualquier agencia a la que le falle el GET de hechos le desaparece el «N esperan tu ok» y el
    // acto Revisa **se pinta como terminado** (`actHead(..., pendingCount === 0)`), que es lo
    // contrario de lo que pasa. El store ya trata esto bien para los aprobados
    // (`approvedCount` devuelve `null` si `loadFailed`); acá faltaba la misma honestidad.
    // No depende del knob ni de la oferta. Hallazgo C-knob-10 del review del 10/09.
    const storeSilent = factsPending === null || factsFailed;
    const pendingCount = clientMemoryLevel === AccessService.MEMORY_LEVEL_TOTAL
        ? (storeSilent ? (pendingFacts || 0) : factsPending)
        : 0;
    // 🔴 Y el resto del mismo hallazgo (C-knob2-07, review del 26/09): el `|| 0` de arriba tapa el
    // caso en que NINGUNA de las dos fuentes sabe. `usePendingFactsFor` devuelve `null` hasta que el
    // poller global contesta, así que con el listado fallado y el poller todavía en vuelo el conteo
    // volvía a ser 0 — y el acto Revisa, a pintarse terminado. Para una pastilla, 0 y «no sé» se
    // dibujan igual (no se muestra); para el ROTULO del acto no, y ahí es donde hay que distinguirlo.
    const pendingUnknown = clientMemoryLevel === AccessService.MEMORY_LEVEL_TOTAL
        && storeSilent
        && (pendingFacts === null || pendingFacts === undefined);
    // El "+" no es cosmético: el listado del backend viene recortado a 500 hechos, y ahí el número
    // deja de ser exacto. Sin el listado (todavía cargando, o falló) no se afirma nada.
    const approvedLabel = approved.n === null
        ? null
        : `${approved.n}${approved.truncated ? '+' : ''}`;

    if (!loaded) {
        return (
            <section className="section-content">
                <ClientHeader name={name}/>
                <ClientTabs clientId={clientId}/>
                <div className="section-body">
                    <div className="box-loader"><Spinner color="positive"/></div>
                </div>
            </section>
        );
    }

    // El Recorrido ES la ficha 2.1. Sin ella, esta ruta no existe y el usuario vuelve a la pantalla
    // de Memoria de siempre — que es exactamente la que redirige acá cuando sí la tiene, así que
    // las dos direcciones esperan a `loaded` para no rebotarse entre sí.
    if (!ficha21) return <Navigate to={`/client/${clientId}/memory`} replace/>;
    if (steps.length === 0) return <Navigate to={`/client/${clientId}/monitoring`} replace/>;

    // Dónde está parado el usuario: lo sabe el observer. El hash es el fallback del PRIMER paint —
    // dice por dónde se entró, y para cuando el observer contesta ya no dice nada útil.
    const hashStep = boosterStepFromHash(location && location.hash);
    const activeStep = activeSection || hashStep || null;
    // La aurora no puede quedarse sin teñir el frame que tarda el observer en responder.
    const auroraStep = activeStep || (walkSteps[0] && walkSteps[0].key) || BOOSTER_STEP_MEMORY;
    // El acto al que apunta el hash se monta entero y ya: es a donde el scroll está por ir, así que
    // llegar y encontrar un placeholder sería llegar a ningún lado.
    const eagerAct = (stepKey) => hashStep === stepKey;

    // Un umbral cuenta la historia entre DOS actos contiguos. Sin Revisa (nivel MEDIO no tiene cola
    // de curado) no son dos umbrales con uno faltante: es uno solo, y dice otra cosa.
    // `{{n}}` y no `{{count}}`: `count` es la variable con la que i18next dispara la pluralización
    // y buscaría claves `_one`/`_other` que no existen.
    const approvedText = approvedLabel === null
        ? null
        : t('ia_booster.flow.threshold_memory_n', {n: approvedLabel, interpolation: {escapeValue: false}});
    const thresholds = [];
    if (showFeed && showReview) {
        thresholds.push({
            after: BOOSTER_STEP_FEED,
            to: BOOSTER_STEP_REVIEW,
            text: t('ia_booster.flow.threshold_review'),
            em: t('ia_booster.flow.threshold_review_em'),
            count: pendingCount > 0 ? t('ia_booster.flow.threshold_review_n', {n: pendingCount}) : null,
            tone: 'warn',
        });
    } else if (showFeed && showMemory) {
        thresholds.push({
            after: BOOSTER_STEP_FEED,
            to: BOOSTER_STEP_MEMORY,
            text: t('ia_booster.flow.threshold_direct'),
            em: t('ia_booster.flow.threshold_direct_em'),
            count: approvedText,
        });
    }
    if (showReview && showMemory) {
        thresholds.push({
            after: BOOSTER_STEP_REVIEW,
            to: BOOSTER_STEP_MEMORY,
            text: t('ia_booster.flow.threshold_memory'),
            em: t('ia_booster.flow.threshold_memory_em'),
            count: approvedText,
        });
    }
    const thresholdAfter = (stepKey) => thresholds.find(th => th.after === stepKey);

    // El `id` del título es el que cada `<section>` referencia con `aria-labelledby`: sin él, un
    // lector de pantalla anuncia cuatro regiones sin nombre y el recorrido deja de ser navegable.
    //
    // El rótulo («01 · ALIMENTA») es del mockup y no es decoración: en una página que se scrollea,
    // el título dice de qué habla la sección pero no en qué parte del recorrido estás. El número y
    // el nombre salen de `BOOSTER_STEP_DEFS` —la misma definición que dibujan el hilo y el
    // sidebar—, así que el `order` es el FIJO del paso y nunca su posición en la lista visible: en
    // nivel MEDIO no hay Revisa y Memoria sigue siendo el 3.
    // El lede del acto Revisa depende de la posición del knob: con «Revisas todo» no entra nada
    // solo, así que la frase por default —que invita a elegir cuánto entra— describiría algo que no
    // está pasando. Sin dato (backend viejo, o sin nivel TOTAL) rige la de siempre, que es cierta en
    // las tres posiciones. Los otros actos no tienen variante.
    const ledeKeyFor = (headKey, isEmpty) => {
        const base = `ia_booster.head.${headKey}_lede`;
        if (isEmpty) return `${base}_empty`;
        if (headKey === 'review' && autonomy.available && autonomy.effective === 'MANUAL') {
            return `${base}_manual`;
        }
        return base;
    };

    const actHead = (stepKey, headKey, isEmpty) => (
        <header className="iab-flow-head">
            {/* El rótulo y el título van en la MISMA línea, como el mockup: el rótulo es el
                sobretítulo de ese título, no una etiqueta suelta encima. */}
            <div className="iab-flow-headline">
                <BoosterActKicker stepKey={stepKey}/>
                <h2 className="iab-title" id={`${stepKey}-title`}>
                    {t(`ia_booster.head.${headKey}_title`, {
                        client: name || t('ia_booster.this_client'),
                        interpolation: {escapeValue: false},
                    })}
                </h2>
            </div>
            <p className="iab-lede">{t(ledeKeyFor(headKey, isEmpty))}</p>
        </header>
    );

    return (
        <section className="section-content">
            <ClientHeader name={name}/>
            <ClientTabs clientId={clientId}/>

            {/* UN solo estado de hechos para toda la página. El mazo de Revisa, su modo lista y el
                archivo del acto Memoria están montados a la vez en el mismo DOM: con estado por
                componente, aprobar una carta dejaba al archivo mostrando ese hecho todavía en la
                cola. No es un problema de refresco — son tres respuestas a la misma pregunta. */}
            <ClientFactsProvider clientId={clientId}
                                 hasFactsLayer={hasFactsLayer}
                                 onPendingCountChange={handlePendingCountChange}
                                 onApprovedCountChange={handleApprovedCountChange}>
            <FactsStoreBridge refetchRef={factsRefetchRef}
                              onLoadFailedChange={handleFactsLoadFailed}/>
            <SourcesDrawerContext.Provider value={drawerApi}>
            {/* El visor del crudo, UNO para toda la página: la carta del mazo, la fila del archivo
                y el ruido archivado abren el mismo modal, posicionado en la cita del hecho. */}
            <SourceViewerHost clientId={clientId}>
            <div className="iab-flow" ref={flowRef}>
                <div className="iab-flow-canvas" data-step={auroraStep}>
                    <BoosterAurora/>

                    <div className="iab-flow-inner">
                        <BoosterThread steps={steps} clientId={clientId} pendingCount={pendingCount}
                                       activeStep={activeStep}/>
                        <BoosterHelpButtons onOpen={setHelpMode} showShortcuts={showReview}/>

                        {showFeed && (
                            <section className="iab-flow-act -opens" id={BOOSTER_STEP_FEED}
                                     aria-labelledby={`${BOOSTER_STEP_FEED}-title`}>
                                <div className="iab-flow-scene">
                                    {/* Sin encabezado: el título que había ("Alimenta la memoria de
                                        X") repetía lo que el orbe ya dice en su leyenda. Lo primero
                                        del acto es la fila del instructivo —el rótulo del paso, por
                                        qué importa lo que se carga y qué material sirve—, arriba
                                        del orbe y visible sin buscarla (card wugYmCtZ: Leo la
                                        encontró sólo porque fede le dijo dónde mirar). El lede
                                        sobrevive SÓLO cuando la memoria está vacía, que es cuando
                                        hace falta que alguien explique de qué se trata. */}
                                    <FeedGuideBlock clientId={clientId}
                                                    clientName={name}
                                                    onPickKind={handlePickKind}
                                                    refreshToken={packToken}/>
                                    {approved.n === 0 && (
                                        <p className="iab-lede -intro">
                                            {t('ia_booster.head.feed_lede_empty')}
                                        </p>
                                    )}
                                    {/* `briefFailed` es, del lado del hero, "el número no se pudo
                                        leer": con eso el orbe dibuja un guion en vez de latir como
                                        si estuviera por llegar. Desde que el número sale del store,
                                        quien puede fallar es el store. */}
                                    <BoosterFeedHero
                                        clientId={clientId}
                                        clientName={name}
                                        factCount={approved.n}
                                        factCountTruncated={approved.truncated}
                                        facts={briefFacts}
                                        briefFailed={factsFailed}
                                        pendingCount={pendingCount}
                                        hasIa={showUseIa}
                                        iaConnected={showUseIa ? mcpConnected : null}
                                        account={showUseIa ? library : null}
                                        onOpenIa={showUseIa ? () => setConnMode(CONN_IA) : undefined}
                                        onOpenAccount={showUseIa ? () => setConnMode(CONN_ACCOUNT) : undefined}
                                        onIngested={handleIngested}
                                        kind={composeKind}
                                        onKindChange={setComposeKind}
                                        omnibarFocusToken={omnibarFocus}
                                    />
                                </div>
                                <div className="iab-flow-scene">
                                    {/* El material que está entrando, con sus estados reales
                                        (en cola → clasificando → listo/falló).
                                        Éste ES el que pollea. La tabla de Fuentes de más abajo
                                        pollea el mismo endpoint, y dos pollers en la misma pantalla
                                        pagan dos veces por el dato — pero el que se apaga es el de
                                        la tabla, no éste: al revés, las filas quedaban clavadas en
                                        "en cola" para siempre y el narrador mudo, porque sus
                                        estados salen justamente de esa vuelta de poll. */}
                                    <FeedActContent clientId={clientId}
                                                    refreshToken={ingestToken}
                                                    lastIngested={lastIngested}
                                                    onDocumentReady={handleDocumentReady}
                                                    onProcessingStateChange={setProcessingState}/>
                                </div>
                                {/* La tabla de Fuentes NO va acá: vive en el panel lateral
                                    (`SourcesDrawer`), que se abre desde la línea de fuentes de
                                    arriba. Inline, el acto terminaba en veinte filas de documentos
                                    más las conexiones de Drive, y el umbral hacia Revisa quedaba a
                                    dos pantallas. */}
                            </section>
                        )}

                        {/* Sin `clientId`: el umbral navega por fragmento (`#revisa`), que el
                            navegador resuelve contra la URL actual sin recargar el documento. */}
                        {thresholdAfter(BOOSTER_STEP_FEED) && (
                            <BoosterThreshold {...thresholdAfter(BOOSTER_STEP_FEED)}/>
                        )}

                        {showReview && (
                            <section className="iab-flow-act" id={BOOSTER_STEP_REVIEW}
                                     aria-labelledby={`${BOOSTER_STEP_REVIEW}-title`}>
                                <div className="iab-flow-scene">
                                    {/* `!pendingUnknown`: con las dos fuentes calladas no se afirma
                                        que la cola esté vacía — ver `pendingUnknown`. */}
                                    {actHead(BOOSTER_STEP_REVIEW, 'review',
                                        pendingCount === 0 && factsNoise === 0 && !pendingUnknown && !processingUnknown && !extractionActive)}
                                </div>
                                {/* Sobre el LIENZO, no dentro de un panel claro: el mazo es un
                                    objeto del escenario (como el orbe o la banda), y encerrarlo en
                                    una caja blanca lo convertía en una tarjeta oscura flotando
                                    dentro de otra tarjeta. Lo claro que queda adentro —las filas de
                                    la «Lista completa»— trae su propia superficie. */}
                                <div className="iab-flow-open">
                                    <LazySection minHeight={PANEL_MIN_HEIGHT.facts}
                                                 eager={eagerAct(BOOSTER_STEP_REVIEW)}>
                                        <BoosterReviewDeck clientId={clientId}
                                                           processingUnknown={processingUnknown}
                                                           extractionActive={extractionActive}
                                                           keyboardEnabled={activeStep === BOOSTER_STEP_REVIEW}
                                                           autonomy={autonomy.available ? autonomy : null}
                                                           onAutonomyChange={autonomy.setValue}
                                                           autonomySaving={autonomy.saving}
                                                           onGoToMemory={() => scrollToAct(BOOSTER_STEP_MEMORY)}/>
                                    </LazySection>
                                </div>
                            </section>
                        )}

                        {thresholdAfter(BOOSTER_STEP_REVIEW) && (
                            <BoosterThreshold {...thresholdAfter(BOOSTER_STEP_REVIEW)}/>
                        )}

                        {showMemory && (
                            <section className="iab-flow-act" id={BOOSTER_STEP_MEMORY}
                                     aria-labelledby={`${BOOSTER_STEP_MEMORY}-title`}>
                                <div className="iab-flow-scene">
                                    {actHead(BOOSTER_STEP_MEMORY, 'memory', false)}
                                </div>
                                {/* El duo: Hechos vs Historial de plataformas, y la bandeja toma el
                                    color de la elegida. Reemplaza a las tres vistas sueltas
                                    (Resumen · Bitácora · Notas) que había acá — decisión de
                                    producto del 28/08: la Bitácora ES la bandeja de Historial y las
                                    Notas son un grupo del archivo.
                                    El ancla `#bitacora` queda AFUERA del duo y envolviéndolo: los
                                    deep-links del Panel, la campanita y el Command Center apuntan
                                    ahí, y el id tiene que existir en el DOM aunque la bandeja
                                    abierta sea la otra. `MemoryDuo` mira el ancla y los params para
                                    abrir directo en Historial — y por eso recibe la `location`: con
                                    el Recorrido ya montado un deep-link nuevo no remonta nada, y
                                    ésta es la única señal de que llegó. */}
                                <div id={BOOSTER_ANCHOR_BITACORA} className="iab-flow-open">
                                    <LazySection minHeight={PANEL_MIN_HEIGHT.bitacora}
                                                 eager={eagerAct(BOOSTER_STEP_MEMORY) || bitacoraDeepLink}>
                                        <MemoryDuo clientId={clientId} location={location}/>
                                    </LazySection>
                                </div>
                            </section>
                        )}

                        <div className="iab-flow-aperture">
                            <span className="iab-flow-thline"/>
                            <span className="iab-flow-aperture-cap">{t('ia_booster.flow.aperture')}</span>
                        </div>
                    </div>
                </div>

                {showUseIa && (
                    <div className="iab-flow-usaband" id={BOOSTER_STEP_USE_IA}>
                        <BoosterUseIaBand clientId={clientId} clientName={name}
                                          connected={!!mcpConnected}/>
                    </div>
                )}
            </div>

            {/* Los overlays de la página. Van adentro del store de hechos (el panel de Fuentes
                borra documentos y el archivo cuenta hechos por documento) y adentro del provider
                del panel (el modal de la cuenta lo abre para conectar Drive). */}
            <SourcesDrawer clientId={clientId} clientName={name}
                           isOpen={sourcesOpen} onClose={closeSources}
                           refreshToken={ingestToken} connectToken={sourcesConnectToken}
                           onIngested={handleIngested}
                           onFactsChanged={handleFactsChanged}/>
            <BoosterHelpModal mode={helpMode} onClose={() => setHelpMode(null)}
                              onModeChange={setHelpMode} showShortcuts={showReview}/>
            <BoosterConnectionModal mode={connMode} clientId={clientId} clientName={name}
                                    connected={!!mcpConnected} library={library}
                                    onClose={() => setConnMode(null)} onOpenSources={openSources}/>
            </SourceViewerHost>
            </SourcesDrawerContext.Provider>
            </ClientFactsProvider>
        </section>
    );
}

export default BoosterFlowContent;
